import React from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, Badge } from 'antd';
import type { MenuProps } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ApiOutlined,
  AppstoreOutlined,
  UserOutlined,
  LogoutOutlined,
  TeamOutlined,
  DollarOutlined,
  CustomerServiceOutlined,
  ThunderboltOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../features/auth/store/authStore';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuthStore();

  const menuItems: MenuProps['items'] = [
    {
      key: 'main',
      type: 'group',
      label: 'PRINCIPAL',
      children: [
        {
          key: '/admin/dashboard',
          icon: <DashboardOutlined style={{ fontSize: 16 }} />,
          label: 'Dashboard',
          onClick: () => navigate('/admin/dashboard'),
        },
      ],
    },
    {
      type: 'divider',
      style: { margin: '8px 0', borderColor: 'rgba(255, 255, 255, 0.15)' },
    },
    {
      key: 'products',
      type: 'group',
      label: 'PRODUTOS',
      children: [
        {
          key: '/admin/workflows',
          icon: <AppstoreOutlined style={{ fontSize: 16 }} />,
          label: (
            <span>
              Workflows
              <Badge 
                count={0} 
                showZero={false}
                style={{ marginLeft: 8, backgroundColor: '#52c41a' }} 
              />
            </span>
          ),
          onClick: () => navigate('/admin/workflows'),
        },
      ],
    },
    {
      type: 'divider',
      style: { margin: '8px 0', borderColor: 'rgba(255, 255, 255, 0.15)' },
    },
    {
      key: 'management',
      type: 'group',
      label: 'GESTÃO',
      children: [
        {
          key: '/admin/clients',
          icon: <TeamOutlined style={{ fontSize: 16 }} />,
          label: 'Usuários',
          onClick: () => navigate('/admin/clients'),
        },
        {
          key: '/admin/payments',
          icon: <DollarOutlined style={{ fontSize: 16 }} />,
          label: 'Pagamentos',
          onClick: () => navigate('/admin/payments'),
        },
        {
          key: '/admin/form-approvals',
          icon: <FormOutlined style={{ fontSize: 16 }} />,
          label: (
            <span>
              Formulários
              <Badge 
                count={0} 
                showZero={false}
                style={{ marginLeft: 8, backgroundColor: '#1890ff' }} 
              />
            </span>
          ),
          onClick: () => navigate('/admin/form-approvals'),
        },
        {
          key: '/admin/support',
          icon: <CustomerServiceOutlined style={{ fontSize: 16 }} />,
          label: (
            <span>
              Suporte
              <Badge 
                count={0} 
                showZero={false}
                style={{ marginLeft: 8, backgroundColor: '#ff4d4f' }} 
              />
            </span>
          ),
          onClick: () => navigate('/admin/support'),
        },
      ],
    },
    {
      type: 'divider',
      style: { margin: '8px 0', borderColor: 'rgba(255, 255, 255, 0.15)' },
    },
    {
      key: 'system',
      type: 'group',
      label: 'SISTEMA',
      children: [
        {
          key: '/admin/n8n-instances',
          icon: <ApiOutlined style={{ fontSize: 16 }} />,
          label: 'Instâncias N8N',
          onClick: () => navigate('/admin/n8n-instances'),
        },
        {
          key: '/admin/forms',
          icon: <FormOutlined style={{ fontSize: 16 }} />,
          label: 'Formulários',
          onClick: () => navigate('/admin/forms'),
        }
      ],
    },
  ];

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Meu Perfil',
      onClick: () => navigate('/admin/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sair',
      danger: true,
      onClick: async () => {
        await signOut();
        navigate('/login');
      },
    },
  ];

  const selectedKey = (() => {
    // Flatten menu items to find selected key
    const flatItems = menuItems
      .filter(item => item && 'children' in item)
      .flatMap(group => (group as any).children || []);
    
    const match = flatItems.find(item => 
      item?.key && location.pathname.startsWith(item.key as string)
    );
    
    return match?.key as string || '/admin/dashboard';
  })();

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        breakpoint="lg"
        collapsedWidth="0"
        width={260}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, #001529 0%, #000c17 100%)',
          boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ 
          height: 72, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          marginBottom: 16,
          background: 'rgba(255, 255, 255, 0.05)',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <ThunderboltOutlined style={{ 
              fontSize: 28, 
              color: '#1890ff',
            }} />
            <div>
              <div style={{
                color: '#fff',
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: '0.5px',
              }}>
                FlowHub
              </div>
              <div style={{
                color: 'rgba(255, 255, 255, 0.45)',
                fontSize: 11,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}>
                Admin Panel
              </div>
            </div>
          </div>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          style={{
            border: 'none',
            background: 'transparent',
          }}
        />
      </Sider>
      <Layout style={{ marginLeft: 260 }}>
        <Header style={{ 
          padding: '0 32px', 
          background: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          zIndex: 1,
        }}>
          <div>
            <Text strong style={{ fontSize: 18, color: '#001529' }}>
              {(() => {
                const flatItems = menuItems
                  .filter(item => item && 'children' in item)
                  .flatMap(group => (group as any).children || []);
                
                const item = flatItems.find(i => i?.key === selectedKey);
                
                if (item && 'label' in item) {
                  // Extract text from label if it's a ReactNode with Badge
                  const label = item.label;
                  if (typeof label === 'string') {
                    return label;
                  }
                  // For complex labels with badges, get the text from props
                  return (label as any)?.props?.children?.[0] || 'Dashboard';
                }
                return 'Dashboard';
              })()}
            </Text>
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div style={{ 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12,
              padding: '8px 12px',
              borderRadius: 8,
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            >
              <Avatar 
                icon={<UserOutlined />} 
                style={{ 
                  backgroundColor: '#1890ff',
                  boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                }} 
              />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text strong style={{ fontSize: 14 }}>{user?.email?.split('@')[0]}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>Administrador</Text>
              </div>
            </div>
          </Dropdown>
        </Header>
        <Content style={{ 
          margin: '24px 24px 0',
          overflow: 'initial',
        }}>
          <div style={{ 
            padding: 32, 
            background: '#fff',
            minHeight: 360,
            borderRadius: 12,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03), 0 2px 4px rgba(0, 0, 0, 0.03)',
          }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};
